import React, { useCallback, useEffect, useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { Button } from '@/components/ui/button'
import { FileIcon, Cross2Icon, ReloadIcon } from '@radix-ui/react-icons'

const UPLOAD_PDF = gql`
  mutation uploadPdf($file: Upload!) {
    uploadPdf(file: $file) {
      pdf_url
      manifest_url
    }
  }
`

// GCS objects send no CORS headers, so the manifest cannot be fetched from the
// browser directly — poll it through the backend instead.
const FLIPBOOK_MANIFEST = gql`
  query flipbookManifest($manifest_url: String!) {
    flipbookManifest(manifest_url: $manifest_url)
  }
`

// Hard cap — must match the backend's MAX_PDF_BYTES; checking client-side too
// avoids streaming 60MB just to get a server rejection.
const MAX_PDF_BYTES = 50 * 1024 * 1024

type ManifestStatus = 'none' | 'processing' | 'ready' | 'failed'

interface PdfUploadV2Props {
  pdfUrl: string | null
  manifestUrl: string | null
  onChange: (value: { pdf_url: string | null; manifest_url: string | null }) => void
}

export function PdfUploadV2({ pdfUrl, manifestUrl, onChange }: PdfUploadV2Props) {
  const [uploadPdf] = useMutation(UPLOAD_PDF)
  const [isUploading, setIsUploading] = useState(false)
  const [status, setStatus] = useState<ManifestStatus>('none')
  const [pageCount, setPageCount] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Poll the manifest (via the backend — see FLIPBOOK_MANIFEST) while the
  // rasterization job runs. `flipbookManifest` returns null until the job
  // writes the manifest, so keep polling on null.
  const { data: manifestData, stopPolling, startPolling } = useQuery(FLIPBOOK_MANIFEST, {
    variables: { manifest_url: manifestUrl ?? '' },
    skip: !manifestUrl,
    pollInterval: 4000,
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (!manifestUrl) {
      setStatus('none')
      return
    }
    // Restart polling on a new manifestUrl. This ensures that stopPolling from
    // a previous terminal manifest doesn't prevent polling on the next upload.
    startPolling(4000)

    const raw = manifestData?.flipbookManifest
    if (!raw) {
      setStatus('processing')
      return
    }
    try {
      const manifest = JSON.parse(raw)
      if (manifest.status === 'ready') {
        setStatus('ready')
        setPageCount(manifest.page_count)
        stopPolling()
      } else if (manifest.status === 'failed') {
        setStatus('failed')
        setError(manifest.error ?? 'Processing failed')
        stopPolling()
      }
    } catch {
      // Malformed manifest — keep polling; the job may still be writing it.
    }
  }, [manifestUrl, manifestData, stopPolling, startPolling])

  const uploadFile = useCallback(
    async (file: File) => {
      setError(null)
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed.')
        return
      }
      if (file.size > MAX_PDF_BYTES) {
        setError(`This PDF is ${(file.size / (1024 * 1024)).toFixed(1)} MB — the limit is 50 MB.`)
        return
      }
      setIsUploading(true)
      try {
        const { data } = await uploadPdf({ variables: { file } })
        if (data?.uploadPdf) {
          onChange({
            pdf_url: data.uploadPdf.pdf_url,
            manifest_url: data.uploadPdf.manifest_url,
          })
        }
      } catch (e: any) {
        console.error('Upload error:', e)
        setError(e?.message ?? 'Upload failed')
      } finally {
        setIsUploading(false)
      }
    },
    [onChange, uploadPdf],
  )

  const handleFileInput = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) await uploadFile(file)
      // Reset input to allow re-selecting the same file after validation or error.
      event.target.value = ''
    },
    [uploadFile],
  )

  const handleRemove = () => {
    onChange({ pdf_url: null, manifest_url: null })
    setStatus('none')
    setPageCount(null)
    setError(null)
  }

  return (
    <div className="col-span-2 w-full">
      {pdfUrl ? (
        <div className="flex items-center gap-3 rounded-md border p-3">
          <FileIcon className="h-5 w-5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm">{pdfUrl.split('/').pop()}</p>
            <p className="text-xs text-muted-foreground">
              {status === 'processing' && (
                <span className="flex items-center gap-1">
                  <ReloadIcon className="h-3 w-3 animate-spin" /> Processing pages…
                </span>
              )}
              {status === 'ready' && <span>Ready — {pageCount} pages</span>}
              {status === 'failed' && <span className="text-destructive">Failed: {error}</span>}
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={handleRemove}>
            <Cross2Icon className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex w-full items-center justify-between gap-4 rounded-lg border-2 border-dashed border-gray-200 p-4">
          <p className="text-sm text-gray-500">Upload the catalog PDF (max 50 MB)</p>
          <input
            type="file"
            id="pdf-upload-input"
            accept="application/pdf"
            className="hidden"
            onChange={handleFileInput}
          />
          <Button
            type="button"
            variant="outline"
            disabled={isUploading}
            onClick={() => document.getElementById('pdf-upload-input')?.click()}
          >
            {isUploading ? <ReloadIcon className="mr-2 h-4 w-4 animate-spin" /> : <FileIcon className="mr-2 h-4 w-4" />}
            {isUploading ? 'Uploading…' : 'Upload PDF'}
          </Button>
        </div>
      )}
      {error && !pdfUrl && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  )
}
